import { escapeSvgAttr } from "./affineImageTriangle";
import {
    buildStampMarkup,
    type ActiveStampStroke,
    type ParsedStampObject,
    type StampRenderOptions,
    type StampStroke,
} from "./artbrushStampBrush";
import {
    buildReferenceImageMarkup,
    type ArtbrushReferenceImage,
} from "./artbrushReference";
import { polylineStrokeOutlineToPathD } from "./pathAgents";

/** Paper fill — shared by canvas viewer and SVG export (WYSIWYG). */
export const ARTBRUSH_PAPER_FILL = "#fafafa";

export interface BrushStrokePoint {
    x: number;
    y: number;
    halfWidth: number;
}

export interface BrushStroke {
    id: string;
    color: string;
    points: BrushStrokePoint[];
}

export interface BrushPointerSample {
    x: number;
    y: number;
    pressure: number;
    pointerType: string;
}

/** Map pointer pressure to 0–1; mouse/touch use full pressure. */
export function effectivePressure(
    pressure: number,
    pointerType: string,
): number {
    if (pointerType !== "pen") return 1;
    if (pressure <= 0) return 0;
    return Math.min(1, pressure);
}

/** Half stroke width at a sample (brushSize = diameter at full pressure). */
export function halfWidthFromPressure(
    pressure: number,
    pointerType: string,
    brushSize: number,
    minPressureFactor: number,
): number {
    const t = effectivePressure(pressure, pointerType);
    const minHalf = (brushSize * minPressureFactor) / 2;
    const maxHalf = brushSize / 2;
    return minHalf + (maxHalf - minHalf) * t;
}

const MIN_POINT_DIST = 0.35;

export function appendSamplesToStroke(
    points: BrushStrokePoint[],
    samples: ReadonlyArray<BrushPointerSample>,
    brushSize: number,
    minPressureFactor: number,
): BrushStrokePoint[] {
    const next = [...points];
    for (const s of samples) {
        const halfWidth = halfWidthFromPressure(
            s.pressure,
            s.pointerType,
            brushSize,
            minPressureFactor,
        );
        const last = next[next.length - 1];
        if (
            last &&
            Math.hypot(last.x - s.x, last.y - s.y) < MIN_POINT_DIST
        ) {
            next[next.length - 1] = { x: s.x, y: s.y, halfWidth };
            continue;
        }
        next.push({ x: s.x, y: s.y, halfWidth });
    }
    return next;
}

function laneOffsetMultipliers(count: number): number[] {
    if (count <= 1) return [0];
    const denom = count - 1;
    return Array.from({ length: count }, (_, i) => i / denom - 0.5);
}

function tangentForSample(
    samples: ReadonlyArray<BrushPointerSample>,
    idx: number,
): { tx: number; ty: number } {
    const prev = idx > 0 ? samples[idx - 1]! : samples[idx]!;
    const next =
        idx < samples.length - 1 ? samples[idx + 1]! : samples[idx]!;
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const len = Math.hypot(dx, dy);
    if (len <= 1e-6) return { tx: 0, ty: -1 };
    return { tx: dx / len, ty: dy / len };
}

export function buildMultiBrushStrokesFromSamples(
    samples: ReadonlyArray<BrushPointerSample>,
    options: {
        baseStrokeId: string;
        color: string;
        brushSize: number;
        minPressureFactor: number;
        strokeCount: number;
        maxSpreadPx: number;
    },
): BrushStroke[] {
    if (samples.length === 0) return [];
    const {
        baseStrokeId,
        color,
        brushSize,
        minPressureFactor,
        strokeCount,
        maxSpreadPx,
    } = options;
    const count = Math.max(1, Math.round(strokeCount));
    const spreadMax = Math.max(0, maxSpreadPx);
    const multipliers = laneOffsetMultipliers(count);

    return multipliers.map((multiplier, laneIdx) => {
        const offsetSamples = samples.map((sample, i) => {
            const p = effectivePressure(sample.pressure, sample.pointerType);
            const spread = p * spreadMax;
            const offset = spread * multiplier;
            const { tx, ty } = tangentForSample(samples, i);
            const nx = -ty;
            const ny = tx;
            return {
                x: sample.x + nx * offset,
                y: sample.y + ny * offset,
                pressure: sample.pressure,
                pointerType: sample.pointerType,
            };
        });
        return {
            id: `${baseStrokeId}-lane-${laneIdx}`,
            color,
            points: appendSamplesToStroke(
                [],
                offsetSamples,
                brushSize,
                minPressureFactor,
            ),
        };
    });
}

function singlePointPathD(x: number, y: number, halfWidth: number): string {
    const r = Math.max(0.5, halfWidth);
    return `M ${x - r} ${y} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0 Z`;
}

/** Strokes drawn on canvas (committed + in-progress). */
export function strokesForRender(
    strokes: ReadonlyArray<BrushStroke>,
    activeStroke: ReadonlyArray<BrushStroke> | null,
): BrushStroke[] {
    return activeStroke ? [...strokes, ...activeStroke] : [...strokes];
}

export interface ArtbrushCanvasMarkupOptions {
    referenceImage?: ArtbrushReferenceImage | null;
    referenceOpacity?: number;
    /** When true, reference `<image>` is included in markup (viewer or export). */
    renderReference?: boolean;
    stampStrokes?: ReadonlyArray<StampStroke>;
    activeStampStroke?: ActiveStampStroke | null;
    stampObjects?: ReadonlyArray<ParsedStampObject>;
    stampRenderOptions?: StampRenderOptions | null;
}

/**
 * Inner SVG markup for the artboard: paper, optional reference, stroke paths.
 * Same layer order as the viewer (no debug overlay).
 */
export function buildArtbrushCanvasInnerMarkup(
    strokes: ReadonlyArray<BrushStroke>,
    activeStroke: ReadonlyArray<BrushStroke> | null,
    width: number,
    height: number,
    options: ArtbrushCanvasMarkupOptions = {},
): string {
    const {
        referenceImage = null,
        referenceOpacity = 0.35,
        renderReference = false,
        stampStrokes = [],
        activeStampStroke = null,
        stampObjects = [],
        stampRenderOptions = null,
    } = options;

    const parts: string[] = [
        `<rect x="0" y="0" width="${width}" height="${height}" fill="${ARTBRUSH_PAPER_FILL}"/>`,
    ];

    if (referenceImage && renderReference) {
        parts.push(
            buildReferenceImageMarkup(referenceImage, referenceOpacity),
        );
    }

    const pathLines = strokesForRender(strokes, activeStroke)
        .map((stroke) => {
            const d = brushStrokeToPathD(stroke);
            if (!d) return "";
            return `<path d="${escapeSvgAttr(d)}" fill="${escapeSvgAttr(stroke.color)}" stroke="none"/>`;
        })
        .filter(Boolean);
    parts.push(...pathLines);

    if (stampObjects.length > 0 && stampRenderOptions) {
        const stampMarkup = buildStampMarkup(
            stampStrokes,
            activeStampStroke,
            stampObjects,
            stampRenderOptions,
        );
        if (stampMarkup) parts.push(stampMarkup);
    }

    return parts.join("\n  ");
}

/** Full SVG document matching the visible canvas (strokes; reference optional). */
export function buildArtbrushExportSvgString(
    strokes: ReadonlyArray<BrushStroke>,
    activeStroke: ReadonlyArray<BrushStroke> | null,
    width: number,
    height: number,
    options: ArtbrushCanvasMarkupOptions = {},
): string {
    const inner = buildArtbrushCanvasInnerMarkup(
        strokes,
        activeStroke,
        width,
        height,
        options,
    );
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">\n  ${inner}\n</svg>`;
}

/** Filled path `d` for a variable-width brush stroke. */
export function brushStrokeToPathD(stroke: BrushStroke): string | null {
    const { points } = stroke;
    if (points.length === 0) return null;
    if (points.length === 1) {
        const p = points[0]!;
        return singlePointPathD(p.x, p.y, p.halfWidth);
    }
    const halfWidths = points.map((p) => p.halfWidth);
    return polylineStrokeOutlineToPathD(points, halfWidths);
}
